import React, { ReactElement, useContext } from 'react'
import cn from 'classnames'
import { TextTypographyProps, Typography } from '@grammarly/badger-ui'
import { DescriptionSizeImage, TypeAssetImage } from '../../../lib/types'
import { ImageContext, GrammarlyAtWork25Context } from '../../../contexts/image-context'
import { ContentProps } from '../../renderer'
import { FixedSpacer } from '../../ui-lib/spacer'
import { ResponsiveImage } from '../../ui-lib/image/responsive-image'
import { useImageLoadingProps, Image } from '../../ui-lib/image'
import { isSvg } from '../../../lib/utils/image'
import { updateAssetCDNUrl } from '../utils'
import { renderRichTextMultiLineContent } from '../../renderer/rich-text'
import styles from './image.module.scss'
import grammarlyAtWork25Styles from '../../dynamic/grammarly-at-work-25/grammarly-at-work-25.module.scss'

const descriptionTypo: Record<DescriptionSizeImage, (props: TextTypographyProps) => ReactElement> = {
  [DescriptionSizeImage.Large]: Typography.TXL,
  [DescriptionSizeImage.Medium]: Typography.C1,
}

export const ImageAsset: React.FC<ContentProps<TypeAssetImage>> = (props) => {
  const { sizes, layout } = useContext(ImageContext)
  const { isGrammarlyAtWork25 } = useContext(GrammarlyAtWork25Context)
  const loadingProps = useImageLoadingProps()
  const { className, structure } = props
  const { title = '', asset, description, isRounded, descriptionSize } = structure.fields || {}
  const DescriptionComponent = descriptionTypo[descriptionSize ?? DescriptionSizeImage.Medium]
  if (!asset || !asset.fields?.file) {
    return null
  }

  const { url, contentType, details } = asset.fields.file || {}
  const { width, height } = details?.image || {}
  const isResponsive = !isSvg(contentType)

  // Apply GrammarlyAtWork25 styles when in that context
  const imageClassName = cn(className, {
    [styles.roundedImage]: isRounded,
    [grammarlyAtWork25Styles.grammarlyHeroImage]: isGrammarlyAtWork25,
  })

  const descriptionContent = description && (
    <>
      <FixedSpacer multiplier={2} />
      <DescriptionComponent as="div">{renderRichTextMultiLineContent(description)}</DescriptionComponent>
    </>
  )

  return isResponsive ? (
    <>
      <ResponsiveImage
        alt={title}
        src={updateAssetCDNUrl(url)}
        className={imageClassName}
        sizes={sizes}
        layout={layout}
        width={width}
        height={height}
        {...loadingProps}
      />
      {descriptionContent}
    </>
  ) : (
    <>
      <Image className={imageClassName} alt={title} src={updateAssetCDNUrl(url)} {...loadingProps} />
      {descriptionContent}
    </>
  )
}
